// ============================================================================
// Payout Batches — the weekly Trolley payout batch
// ============================================================================
// Every Wednesday a DRAFT batch is built with every hire that can be paid right
// now (Refresh builds it by hand too). The draft only exists in our database:
// lines can be removed or lowered freely, and a hire in it can still be paid on
// its own from Creator Payments — the batch skips it at send.
//
// Confirm creates ONE Trolley batch with every line and starts it. From then on
// each line is an ordinary Trolley payout: its hire shows "In payout batch" and
// can't be paid twice; a payment that fails frees its hire to be paid again.
//
// The single "Send via Trolley" on a hire is unchanged — admins pick either.
// ============================================================================

import { useMemo, useState, type ReactNode } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { format } from 'date-fns'
import { AlertTriangle, CheckCircle2, ChevronLeft, Layers, Loader2, RefreshCw, Send, Undo2, X } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { usd } from '@/lib/refunds'
import {
  buildPayoutBatch, cancelPayoutBatch, confirmPayoutBatch, payoutBatchOverview, payoutBatchStatusLabel,
  setPayoutBatchItem, syncPayoutBatch, trolleyStatusLabel,
  type PayoutBatch, type PayoutBatchItem, type PayoutTone, type TrolleyPayoutStatus,
} from '@/lib/trolley-payouts'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { DataPagination, PAGE_SIZE_OPTIONS } from '@/components/DataPagination'

const TONE: Record<PayoutTone, string> = {
  blue:    'border-blue-200 bg-blue-50 text-blue-900',
  amber:   'border-amber-200 bg-amber-50 text-amber-900',
  emerald: 'border-green-200 bg-green-50 text-green-900',
  red:     'border-red-200 bg-red-50 text-red-900',
  slate:   'border-slate-200 bg-slate-50 text-slate-800',
}

interface LinePayout {
  id: string
  status: TrolleyPayoutStatus
  amount_cents: number
  trolley_status: string | null
  error_message: string | null
}

interface Line extends PayoutBatchItem {
  cr_hires: {
    id: string
    brand_id: string | null
    videos_required: number | null
    package_size: number | null
    roster_creators: { name: string | null; tiktok_username: string | null } | null
    cr_campaigns: { name: string | null } | null
  } | null
  cr_hire_payouts: LinePayout | null
}

const OPEN_STATUSES = ['draft', 'processing', 'needs_reconciliation']

export default function PayoutBatches() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const selectedId = params.get('batch')
  const [building, setBuilding] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)

  const overview = useQuery({ queryKey: ['payout-batch-overview'], queryFn: payoutBatchOverview, staleTime: 60_000 })
  const canSend = overview.data?.can_send === true

  const openBatch = useQuery({
    queryKey: ['payout-batch-open'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cr_payout_batches')
        .select('*')
        .in('status', OPEN_STATUSES)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle()
      if (error) throw error
      return (data as PayoutBatch) ?? null
    },
  })

  const history = useQuery({
    queryKey: ['payout-batch-history', page, pageSize],
    queryFn: async () => {
      const from = (page - 1) * pageSize
      const { data, error, count } = await supabase
        .from('cr_payout_batches')
        .select('*', { count: 'exact' })
        .eq('status', 'sent')
        .order('sent_at', { ascending: false })
        .range(from, from + pageSize - 1)
      if (error) throw error
      return { rows: (data ?? []) as PayoutBatch[], total: count ?? 0 }
    },
  })

  const selected = useQuery({
    queryKey: ['payout-batch', selectedId],
    enabled: !!selectedId,
    queryFn: async () => {
      const { data, error } = await supabase.from('cr_payout_batches').select('*').eq('id', selectedId).maybeSingle()
      if (error) throw error
      return (data as PayoutBatch) ?? null
    },
  })

  const shown: PayoutBatch | null = selectedId ? selected.data ?? null : openBatch.data ?? null

  const refreshAll = () => {
    queryClient.invalidateQueries({ queryKey: ['payout-batch-open'] })
    queryClient.invalidateQueries({ queryKey: ['payout-batch-history'] })
    queryClient.invalidateQueries({ queryKey: ['payout-batch'] })
    queryClient.invalidateQueries({ queryKey: ['payout-batch-lines'] })
    queryClient.invalidateQueries({ queryKey: ['creator-payments-all'] })
  }

  async function build() {
    setBuilding(true)
    try {
      const r = await buildPayoutBatch()
      toast.success(`Batch refreshed — ${r.included} line${r.included === 1 ? '' : 's'} ready${r.blocked ? `, ${r.blocked} blocked` : ''}.`)
      if (selectedId && selectedId !== r.batch_id) setParams({})
      refreshAll()
    } catch (e) {
      toast.error((e as Error).message)
    } finally {
      setBuilding(false)
    }
  }

  const totalPages = Math.max(1, Math.ceil((history.data?.total ?? 0) / pageSize))
  const loading = selectedId ? selected.isLoading : openBatch.isLoading

  return (
    <div className="space-y-6">
      <div>
        <Button variant="ghost" size="sm" className="gap-1 -ml-2 text-muted-foreground" onClick={() => navigate('/creator-payments')}>
          <ChevronLeft className="h-4 w-4" /> Back to Payments
        </Button>
      </div>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Payout Batches</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Every Wednesday a batch is prepared with every creator who can be paid. Review it, then send it to Trolley in one click.
            Hires can still be paid one at a time from <Link to="/creator-payments" className="underline">Creator Payments</Link>.
          </p>
        </div>
        <div className="flex gap-2">
          {selectedId && (
            <Button variant="outline" size="sm" onClick={() => setParams({})}>Current batch</Button>
          )}
          {(!openBatch.data || openBatch.data.status === 'draft') && (
            <Button size="sm" variant="outline" onClick={build} disabled={building}>
              {building ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
              {openBatch.data ? 'Refresh batch' : 'Prepare batch now'}
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <Skeleton className="h-48 w-full" />
      ) : shown ? (
        <BatchView key={shown.id} batch={shown} canSend={canSend} onChanged={refreshAll} />
      ) : (
        <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
          <Layers className="h-6 w-6 mx-auto mb-2 opacity-60" />
          No batch is open. The next one is prepared automatically on Wednesday — or press <strong>Prepare batch now</strong>.
        </div>
      )}

      <div className="space-y-3">
        <h2 className="text-base font-semibold">Sent batches</h2>
        {history.isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : (history.data?.rows.length ?? 0) === 0 ? (
          <p className="text-sm text-muted-foreground">No batches sent yet.</p>
        ) : (
          <>
            <div className="rounded-lg border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Sent</TableHead>
                    <TableHead>Payments</TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead>By</TableHead>
                    <TableHead>Trolley</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.data!.rows.map(b => (
                    <TableRow key={b.id} className={b.id === selectedId ? 'bg-muted/50' : undefined}>
                      <TableCell className="whitespace-nowrap">{b.sent_at ? format(new Date(b.sent_at), 'MMM d, yyyy h:mm a') : '—'}</TableCell>
                      <TableCell>{b.payment_count ?? '—'}</TableCell>
                      <TableCell className="tabular-nums">{b.total_cents != null ? usd(b.total_cents) : '—'}</TableCell>
                      <TableCell className="text-xs break-all">{b.confirmed_by_email ?? '—'}</TableCell>
                      <TableCell className="text-xs">{b.trolley_status ?? '—'}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="outline" onClick={() => setParams({ batch: b.id })}>View</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <DataPagination
              currentPage={page}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={history.data!.total}
              onPageChange={setPage}
              onPageSizeChange={(s) => { setPageSize(s); setPage(1) }}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
            />
          </>
        )}
      </div>
    </div>
  )
}

// ── One batch ───────────────────────────────────────────────────────────────

function BatchView({ batch, canSend, onChanged }: { batch: PayoutBatch; canSend: boolean; onChanged: () => void }) {
  const [busy, setBusy] = useState<'sync' | 'cancel' | null>(null)
  const [syncNote, setSyncNote] = useState<string | null>(null)
  const [cancellable, setCancellable] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)

  const lines = useQuery({
    queryKey: ['payout-batch-lines', batch.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cr_payout_batch_items')
        .select('*, cr_hires(id, brand_id, videos_required, package_size, roster_creators(name, tiktok_username), cr_campaigns(name)), cr_hire_payouts(id, status, amount_cents, trolley_status, error_message)')
        .eq('batch_id', batch.id)
        .order('created_at', { ascending: true })
      if (error) throw error
      return (data ?? []) as unknown as Line[]
    },
  })

  const brandIds = useMemo(
    () => [...new Set((lines.data ?? []).map(l => l.cr_hires?.brand_id).filter(Boolean))] as string[],
    [lines.data],
  )
  const brands = useQuery({
    queryKey: ['payout-batch-brands', brandIds],
    enabled: brandIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase.from('brand_configs').select('id, brand_name').in('id', brandIds)
      if (error) throw error
      return new Map(((data ?? []) as any[]).map(b => [b.id as string, (b.brand_name as string) ?? b.id]))
    },
  })

  const all = lines.data ?? []
  const included = all.filter(l => l.state === 'included')
  const blocked = all.filter(l => l.state === 'blocked')
  const removed = all.filter(l => l.state === 'removed')
  const includedTotal = included.reduce((s, l) => s + l.amount_cents, 0)
  const status = payoutBatchStatusLabel(batch.status)
  const isDraft = batch.status === 'draft'
  const sentLines = included.filter(l => l.cr_hire_payouts && l.cr_hire_payouts.status !== 'cancelled')

  async function sync() {
    setBusy('sync')
    try {
      const r = await syncPayoutBatch(batch.id)
      setSyncNote(r.note)
      setCancellable(r.cancellable)
      if (r.batch.status !== batch.status) toast.success(`Batch is now: ${payoutBatchStatusLabel(r.batch.status).label}`)
      onChanged()
    } catch (e) {
      toast.error((e as Error).message)
    } finally {
      setBusy(null)
    }
  }

  async function cancel() {
    if (!window.confirm('Cancel this send? Trolley has confirmed no money moved. The batch goes back to draft so it can be sent again.')) return
    setBusy('cancel')
    try {
      await cancelPayoutBatch(batch.id)
      toast.success('Send cancelled — no money was sent. The batch is a draft again.')
      setCancellable(false)
      setSyncNote(null)
      onChanged()
    } catch (e) {
      toast.error((e as Error).message)
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="space-y-4">
      <div className={`rounded-lg border px-4 py-3 space-y-1.5 ${TONE[status.tone]}`}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="font-semibold">
            {isDraft ? 'This week\'s batch' : 'Batch'} · {status.label}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {!isDraft && (
              <Button size="sm" variant="outline" className="h-8 bg-white/60" onClick={sync} disabled={busy !== null}>
                <RefreshCw className={`h-3.5 w-3.5 mr-1 ${busy === 'sync' ? 'animate-spin' : ''}`} /> Sync with Trolley
              </Button>
            )}
            {cancellable && canSend && (
              <Button size="sm" variant="outline" className="h-8 bg-white/60 border-red-300 text-red-700" onClick={cancel} disabled={busy !== null}>
                Cancel send
              </Button>
            )}
            {isDraft && canSend && (
              <Button size="sm" className="h-8" onClick={() => setConfirmOpen(true)} disabled={included.length === 0 || lines.isLoading}>
                <Send className="h-3.5 w-3.5 mr-1" /> Confirm &amp; send {usd(includedTotal)}
              </Button>
            )}
          </div>
        </div>
        <p className="text-xs opacity-90">
          {isDraft && <>Nothing has been sent to Trolley yet. Remove or lower any line, then confirm. </>}
          {batch.status === 'processing' && <>The batch is being sent to Trolley. Its hires can&apos;t be paid separately until this finishes. </>}
          {batch.status === 'needs_reconciliation' && <>We lost contact with Trolley while sending. Press Sync to find out what happened — its hires stay locked until then. </>}
          {batch.status === 'sent' && <>Each hire is marked paid once Trolley confirms its payment. A failed payment frees its hire to be paid again. </>}
        </p>
        {batch.error_message && <p className="text-xs font-medium break-words">{batch.error_message}</p>}
        {syncNote && <p className="text-xs italic">{syncNote}</p>}
        <p className="text-[11px] opacity-75 break-all">
          Prepared by {batch.created_by_email} · {format(new Date(batch.created_at), 'MMM d, yyyy h:mm a')}
          {batch.last_built_at && <> · refreshed {format(new Date(batch.last_built_at), 'MMM d, h:mm a')}</>}
          {batch.confirmed_by_email && <> · confirmed by {batch.confirmed_by_email}</>}
          {batch.trolley_batch_id && <> · Trolley batch {batch.trolley_batch_id}</>}
          {batch.trolley_status && <> · Trolley: {batch.trolley_status}</>}
        </p>
        {!canSend && isDraft && (
          <p className="text-xs">You can view this batch, but sending it needs the payouts.send permission.</p>
        )}
      </div>

      {lines.isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : lines.isError ? (
        <p className="text-sm text-red-600">{(lines.error as Error).message}</p>
      ) : isDraft ? (
        <>
          <LineTable
            title={`To be paid (${included.length}) · ${usd(includedTotal)}`}
            lines={included}
            brands={brands.data}
            empty="No lines to pay. Press Refresh batch to pick up hires that became payable."
            render={l => <DraftLineActions line={l} canSend={canSend} onChanged={onChanged} />}
            editableAmount={canSend}
            onChanged={onChanged}
          />
          {blocked.length > 0 && (
            <LineTable
              title={`Can't be paid via Trolley right now (${blocked.length})`}
              subtitle="Fix the reason, then Refresh the batch — or pay these from the hire page."
              lines={blocked}
              brands={brands.data}
              showReason
            />
          )}
          {removed.length > 0 && (
            <LineTable
              title={`Removed (${removed.length})`}
              lines={removed}
              brands={brands.data}
              showReason
              render={l => canSend && (
                <Button size="sm" variant="ghost" className="h-7" onClick={() => setLine(l, 'included', onChanged)}>
                  <Undo2 className="h-3.5 w-3.5 mr-1" /> Add back
                </Button>
              )}
            />
          )}
        </>
      ) : (
        <>
          <LineTable
            title={`Payments (${sentLines.length})`}
            lines={sentLines}
            brands={brands.data}
            empty="No payments were created for this batch."
            render={l => <SentLineStatus line={l} />}
          />
          {blocked.length > 0 && (
            <LineTable
              title={`Not sent (${blocked.length})`}
              subtitle="These lines were left out when the batch was sent."
              lines={blocked}
              brands={brands.data}
              showReason
            />
          )}
        </>
      )}

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        batch={batch}
        count={included.length}
        totalCents={includedTotal}
        onDone={onChanged}
      />
    </div>
  )
}

// ── Lines ───────────────────────────────────────────────────────────────────

function LineTable({ title, subtitle, lines, brands, empty, render, showReason, editableAmount, onChanged }: {
  title: string
  subtitle?: string
  lines: Line[]
  brands: Map<string, string> | undefined
  empty?: string
  render?: (l: Line) => ReactNode
  showReason?: boolean
  editableAmount?: boolean
  onChanged?: () => void
}) {
  return (
    <div className="space-y-2">
      <div>
        <h3 className="text-sm font-semibold">{title}</h3>
        {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
      </div>
      {lines.length === 0 ? (
        empty ? <p className="text-sm text-muted-foreground">{empty}</p> : null
      ) : (
        <div className="rounded-lg border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Creator</TableHead>
                <TableHead>Brand</TableHead>
                <TableHead>Campaign</TableHead>
                <TableHead>Videos</TableHead>
                <TableHead>Amount</TableHead>
                {showReason && <TableHead>Reason</TableHead>}
                {render && <TableHead className="text-right" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {lines.map(l => {
                const h = l.cr_hires
                return (
                  <TableRow key={l.id}>
                    <TableCell>
                      <Link to={`/creator-payments/hires/${l.hire_id}`} className="font-medium hover:underline">
                        {h?.roster_creators?.name ?? h?.roster_creators?.tiktok_username ?? '—'}
                      </Link>
                      {h?.roster_creators?.tiktok_username && (
                        <p className="text-xs text-muted-foreground">@{h.roster_creators.tiktok_username}</p>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">{(h?.brand_id && brands?.get(h.brand_id)) ?? '—'}</TableCell>
                    <TableCell className="text-sm max-w-[220px] break-words">{h?.cr_campaigns?.name ?? '—'}</TableCell>
                    <TableCell className="text-sm">{h?.videos_required ?? h?.package_size ?? '—'}</TableCell>
                    <TableCell>
                      {editableAmount && onChanged
                        ? <AmountCell line={l} onChanged={onChanged} />
                        : <span className="tabular-nums">{usd(l.cr_hire_payouts?.amount_cents ?? l.amount_cents)}</span>}
                      {l.amount_cents < l.earned_cents && (
                        <p className="text-[11px] text-muted-foreground">earned {usd(l.earned_cents)}</p>
                      )}
                      {l.warning && (
                        <p className="text-[11px] text-amber-700 flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3 shrink-0" /> {l.warning}
                        </p>
                      )}
                    </TableCell>
                    {showReason && <TableCell className="text-xs max-w-[320px] break-words">{l.reason ?? '—'}</TableCell>}
                    {render && <TableCell className="text-right">{render(l)}</TableCell>}
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}

async function setLine(l: Line, state: 'included' | 'removed', onChanged: () => void, amountCents?: number) {
  try {
    await setPayoutBatchItem({ itemId: l.id, state, amountCents })
    onChanged()
    return true
  } catch (e) {
    toast.error((e as Error).message)
    return false
  }
}

function DraftLineActions({ line, canSend, onChanged }: { line: Line; canSend: boolean; onChanged: () => void }) {
  if (!canSend) return null
  return (
    <Button size="sm" variant="ghost" className="h-7 text-red-700" onClick={() => setLine(line, 'removed', onChanged)}>
      <X className="h-3.5 w-3.5 mr-1" /> Remove
    </Button>
  )
}

function AmountCell({ line, onChanged }: { line: Line; onChanged: () => void }) {
  const [value, setValue] = useState(String(line.amount_cents / 100))
  const [saving, setSaving] = useState(false)
  const cents = Math.round(Number(value) * 100)
  const valid = Number.isFinite(cents) && cents > 0 && cents <= line.earned_cents
  const dirty = cents !== line.amount_cents

  async function save() {
    if (!valid || !dirty) return
    setSaving(true)
    const ok = await setLine(line, 'included', onChanged, cents)
    if (!ok) setValue(String(line.amount_cents / 100))
    setSaving(false)
  }

  return (
    <div className="flex items-center gap-1">
      <Input
        type="number" min="0.01" step="0.01" max={line.earned_cents / 100}
        value={value}
        onChange={e => setValue(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') save() }}
        className={`h-8 w-28 ${valid ? '' : 'border-red-400'}`}
        aria-label="Amount (USD)"
      />
      {dirty && (
        <Button size="sm" variant="outline" className="h-8" onClick={save} disabled={!valid || saving}>
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save'}
        </Button>
      )}
    </div>
  )
}

function SentLineStatus({ line }: { line: Line }) {
  const p = line.cr_hire_payouts
  if (!p) return <span className="text-xs text-muted-foreground">—</span>
  const s = trolleyStatusLabel(p)
  const freed = ['failed', 'returned', 'cancelled'].includes(p.status)
  return (
    <div className="flex flex-col items-end gap-1">
      <Badge variant="outline" className={TONE[s.tone]}>
        {p.status === 'processed' && <CheckCircle2 className="h-3 w-3 mr-1" />}
        {s.label}
      </Badge>
      {p.error_message && <span className="text-[11px] text-red-700 max-w-[260px] text-right break-words">{p.error_message}</span>}
      {freed && (
        <Link to={`/creator-payments/hires/${line.hire_id}`} className="text-[11px] underline">Pay on its own</Link>
      )}
    </div>
  )
}

// ── Confirm ─────────────────────────────────────────────────────────────────

function ConfirmDialog({ open, onOpenChange, batch, count, totalCents, onDone }: {
  open: boolean
  onOpenChange: (o: boolean) => void
  batch: PayoutBatch
  count: number
  totalCents: number
  onDone: () => void
}) {
  const [checked, setChecked] = useState(false)
  const [sending, setSending] = useState(false)

  async function send() {
    setSending(true)
    try {
      const r = await confirmPayoutBatch(batch.id)
      toast.success(r.message, { duration: 10_000 })
      if (r.skipped.length > 0) {
        toast.warning(`${r.skipped.length} line${r.skipped.length === 1 ? ' was' : 's were'} skipped — see "Not sent" for the reasons.`, { duration: 12_000 })
      }
      onOpenChange(false)
    } catch (e) {
      toast.error((e as Error).message, { duration: 12_000 })
      onOpenChange(false)
    } finally {
      setSending(false)
      setChecked(false)
      onDone()
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!sending) { onOpenChange(o); setChecked(false) } }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Send this payout batch?</DialogTitle>
          <DialogDescription>
            {count} payment{count === 1 ? '' : 's'} totalling <strong>{usd(totalCents)}</strong> will go out from the BMC Trolley balance in one Trolley batch.
          </DialogDescription>
        </DialogHeader>
        <ul className="text-xs text-muted-foreground list-disc pl-4 space-y-1">
          <li>Every line is checked again first. Any hire that was paid separately or can no longer be paid is skipped.</li>
          <li>If the Trolley balance can&apos;t cover the total, Trolley refuses the whole batch and nothing is sent.</li>
          <li>While it processes, these hires can&apos;t be paid on their own. A payment that fails can be paid again afterwards.</li>
        </ul>
        <label className="flex items-start gap-2 text-sm cursor-pointer">
          <Checkbox checked={checked} onCheckedChange={v => setChecked(v === true)} className="mt-0.5" />
          <span>Send <strong>{usd(totalCents)}</strong> to {count} creator{count === 1 ? '' : 's'}. This can&apos;t be undone from here.</span>
        </label>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>Cancel</Button>
          <Button onClick={send} disabled={!checked || sending || count === 0}>
            {sending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
            {sending ? 'Sending…' : `Send ${usd(totalCents)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
